import { CallState } from "@b2bportal/core-types";
import { createSelector } from "@reduxjs/toolkit";
import type { ActivitiesState } from "../../../store";
import { getParticipantCountFromAgeBandSelection } from "../../../utilities/participantHelpers";

export const getActivitiesShopState = (state: ActivitiesState) =>
  state.activities.activitiesShop;

export const getShopActivityId = (state: ActivitiesState) =>
  state.activities.activitiesShop.shopSearch?.activityId;

export const getShopSearch = (state: ActivitiesState) =>
  state.activities.activitiesShop.shopSearch;

export const getShopCallState = (state: ActivitiesState) =>
  state.activities.activitiesShop.shopCallState;

export const getShopAvailabilityCallState = (state: ActivitiesState) =>
  state.activities.activitiesShop.availabilityCallState;

export const getActivityShop = (state: ActivitiesState) =>
  state.activities.activitiesShop.shop;

export const getActivitySelection = (state: ActivitiesState) =>
  state.activities.activitiesShop.activity;

export const getCancellationPolicy = (state: ActivitiesState) =>
  state.activities.activitiesShop.activity?.cancellationPolicy;

export const getShopData = createSelector(getActivitiesShopState, (state) => {
  return {
    activity: state.shop?.value,
    isLoading: state.shopCallState === CallState.InProcess,
    isError: state.shopCallState === CallState.Failed,
    isUnavailable: state.isActivityUnavailable,
  };
});

export const getFromDate = createSelector(getShopSearch, (search) => {
  return search?.dateRange.from;
});

export const getUntilDate = createSelector(getShopSearch, (search) => {
  return search?.dateRange.until;
});

export const getSelectedBookableItem = (state: ActivitiesState) =>
  state.activities.activitiesShop.selectedBookableItem;

export const getShopOptionByOptionId = createSelector(
  [getActivitiesShopState, getSelectedBookableItem],
  (shopState, selectedBookableItem) => {
    const optionId = selectedBookableItem?.optionId?.value ?? "default";
    return shopState.shopOptions[optionId];
  }
);

export const getSelectedPriceBreakdown = createSelector(
  [getActivitiesShopState, getShopOptionByOptionId],
  (state, shopOption) => {
    const breakdowns = shopOption?.breakdowns;
    const selectedTimeSlot = state.selectedTimeSlot;

    if (breakdowns == null || breakdowns.length === 0) {
      return undefined;
    }

    // Find breakdown matching the selected time slot, or fall back to first
    if (selectedTimeSlot?.startTime != null) {
      const matchingBreakdown = breakdowns.find(
        (breakdown) => breakdown.startTime === selectedTimeSlot.startTime
      );
      if (matchingBreakdown != null) {
        return matchingBreakdown;
      }
    }

    return breakdowns[0];
  }
);

export const getPriceBreakdownLineItems = createSelector(
  [getSelectedPriceBreakdown],
  (breakdown) => breakdown?.lineItems
);

export const getTotalPrice = createSelector(
  [getSelectedPriceBreakdown],
  (breakdown) => breakdown?.totalPriceWithFees
);

export const getOpaqueShoppedProduct = createSelector(
  [getSelectedPriceBreakdown],
  (breakdown) => breakdown?.opaqueShoppedProduct
);

export const getShopOptionCallState = (state: ActivitiesState) =>
  state.activities.activitiesShop.shopOptionCallState;

export const getSelectedDate = (state: ActivitiesState) =>
  state.activities.activitiesShop.selectedDate;

export const getSelectedParticipants = (state: ActivitiesState) =>
  state.activities.activitiesShop.selectedParticipants;

export const getParticipantCount = (state: ActivitiesState) =>
  getParticipantCountFromAgeBandSelection(
    state.activities.activitiesShop.selectedParticipants
  );

export const getMaximumTravelerCount = (state: ActivitiesState) =>
  state.activities.activitiesShop.activity?.bookingRequirements?.maxTravelers;

export const getActivityAgeBands = (state: ActivitiesState) =>
  state.activities.activitiesShop.activity?.ageBands;

export const getActivityBookableItems = (state: ActivitiesState) =>
  state.activities.activitiesShop.activity.bookableItems;

export const getSelectedTimeSlot = (state: ActivitiesState) =>
  state.activities.activitiesShop.selectedTimeSlot;

export const getTicketTypes = (state: ActivitiesState) =>
  state.activities.activitiesShop.activity.ticketInfo?.ticketTypes;

export const getTimeSlots = createSelector(
  [getSelectedBookableItem, getSelectedDate],
  (selectedBookableItem, selectedDate) =>
    selectedBookableItem?.timeSlotsByDate?.[selectedDate] ?? []
);

export const getRatingFromActivity = (state: ActivitiesState) =>
  state.activities.activitiesShop.activity?.rating;

export const getReviews = (state: ActivitiesState) =>
  state.activities.activitiesShop.reviews;

export const getReviewSummary = (state: ActivitiesState) =>
  state.activities.activitiesShop.reviewSummary;

export const getBookingQuestions = (state: ActivitiesState) =>
  state.activities.activitiesShop.activity?.bookingQuestions;

export const getSelectedBookableItemLanguageGuides = createSelector(
  [getSelectedBookableItem],
  (selectedBookableItem) => selectedBookableItem?.languageGuides
);

export const getIsPickupIncluded = createSelector(
  [getSelectedBookableItem],
  (selectedBookableItem) => selectedBookableItem?.isPickupIncluded ?? false
);

export const getPrepareFulfillmentData = createSelector(
  [
    getActivitiesShopState,
    getActivitySelection,
    getSelectedPriceBreakdown,
    getSelectedBookableItem,
    getSelectedDate,
    getSelectedTimeSlot,
    getParticipantCount,
  ],
  (
    shopState,
    activity,
    priceBreakdown,
    selectedBookableItem,
    selectedDate,
    selectedTimeSlot,
    participantCount
  ) => {
    const startLocation = activity?.logistics?.startLocations?.[0];

    return {
      totalPurchasePrice: priceBreakdown?.totalPriceWithFees?.fiat,
      startTime: selectedTimeSlot?.startTime,
      activityId: shopState.shopSearch?.activityId
        ? { value: shopState.shopSearch.activityId }
        : undefined,
      productTitle: activity?.name,
      numberOfTravelers: participantCount,
      activityOptionId: selectedBookableItem?.optionId,
      startLocation,
      durationMinutes: activity?.duration,
      category: "activity", // Default category - ShopResults doesn't have tags
      startDate: selectedDate,
    };
  }
);

export const getTravelerBookingQuestions = createSelector(
  [getBookingQuestions],
  (bookingQuestions) => bookingQuestions?.perTraveler
);

export const getLastShopOptionRequest = (state: ActivitiesState) =>
  state.activities.activitiesShop.lastShopOptionRequest;
